import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Truck } from "lucide-react";
import Seo from "@/components/Seo";

// Loaded lazily (only on this page, only once) rather than in public/index.html, so it never
// costs anything on pages that don't need it.
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Checkout() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Online");
  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    line1: "",
    line2: "",
    landmark: "",
    city: "",
    state: "",
    pincode: localStorage.getItem("yp_pin") || "",
    address_type: "Home",
    notes: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Defense-in-depth: this page is only reachable while signed in with a verified email — the
  // Cart page already gates "Proceed to Checkout" for the normal flow, but this also covers
  // direct navigation (a bookmark, back button, or someone editing the URL by hand).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error("Please sign in to continue to checkout");
      nav("/login?redirect=/checkout");
    } else if (!user.email_verified) {
      nav("/verify-email?send=1");
    }
  }, [user, authLoading, nav]);

  if (authLoading || !user || !user.email_verified) {
    return <div className="mx-auto max-w-2xl px-4 py-24 text-center text-[var(--yp-muted)]">Loading…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-serif-display text-3xl text-[var(--yp-primary)]">Cart is empty</h1>
        <Link to="/shop" className="mt-4 inline-block btn-primary rounded-full px-6 py-3 text-sm font-medium">Continue shopping</Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "phone", "line1", "city", "state", "pincode"];
    for (const k of required) if (!form[k]) return toast.error("Please fill in all delivery details");
    if (form.pincode.length !== 6) return toast.error("Enter a valid 6-digit pincode");
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, variant_id: i.variant_id, name: i.name, image: i.image,
          price: i.price, quantity: i.quantity, size: i.size,
        })),
        address: {
          full_name: form.full_name, phone: form.phone,
          line1: form.line1, city: form.city, state: form.state, pincode: form.pincode,
        },
        payment_method: paymentMethod,
        subtotal, discount: 0, shipping, total,
        email: form.email || "",
      };
      const data = await api.createOrder(payload);

      if (data.razorpay) {
        // Online method + Razorpay configured server-side: the order already exists
        // ("pending") and only flips to "paid" once we verify Razorpay's signed response —
        // never just because the checkout widget resolved.
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error("Could not load the payment gateway. Please try again.");
          setSubmitting(false);
          return;
        }
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id,
          order_id: data.razorpay.order_id,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          name: "YourPlants.in",
          description: `Order ${data.id}`,
          prefill: { name: form.full_name, email: form.email, contact: form.phone },
          theme: { color: "#1B4D3E" },
          handler: async (response) => {
            try {
              await api.verifyPayment(data.id, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              clear();
              toast.success(`Order placed · ${data.id}`);
              nav(`/order/${data.id}`);
            } catch (err) {
              toast.error(err.message || "Payment verification failed");
              setSubmitting(false);
            }
          },
          modal: {
            // Order still exists (pending), cart is left as-is so they can retry from the
            // order page without re-adding everything.
            ondismiss: () => setSubmitting(false),
          },
        });
        rzp.on("payment.failed", () => {
          toast.error("Payment failed. Please try again.");
          setSubmitting(false);
        });
        rzp.open();
        return;
      }

      clear();
      toast.success(`Order placed · ${data.id}`);
      nav(`/order/${data.id}`);
      setSubmitting(false);
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-4 py-10 md:py-14">
      <Seo title="Checkout" path="/checkout" noindex={true} />
      <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)]">Checkout</h1>

      <form onSubmit={onSubmit} className="grid lg:grid-cols-[1fr_360px] gap-8 mt-8">
        <div className="space-y-6">
          <section className="bg-white border border-[var(--yp-border)] rounded-2xl p-6">
            <div className="font-serif-display text-xl text-[var(--yp-primary)]">Delivery details</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field id="checkout-name" label="Full name" value={form.full_name} onChange={(v) => upd("full_name", v)} required />
              <Field id="checkout-phone" label="Mobile" value={form.phone} onChange={(v) => upd("phone", v.replace(/\D/g, ""))} required />
              <Field id="checkout-email" label="Email (optional)" type="email" value={form.email} onChange={(v) => upd("email", v)} className="sm:col-span-2" />
              <Field id="checkout-line1" label="Address line 1" value={form.line1} onChange={(v) => upd("line1", v)} required className="sm:col-span-2" />
              <Field id="checkout-line2" label="Address line 2" value={form.line2} onChange={(v) => upd("line2", v)} className="sm:col-span-2" />
              <Field id="checkout-landmark" label="Landmark" value={form.landmark} onChange={(v) => upd("landmark", v)} />
              <Field id="checkout-pincode" label="Pincode" value={form.pincode} onChange={(v) => upd("pincode", v.replace(/\D/g, "").slice(0, 6))} required />
              <Field id="checkout-city" label="City" value={form.city} onChange={(v) => upd("city", v)} required />
              <Field id="checkout-state" label="State" value={form.state} onChange={(v) => upd("state", v)} required />
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)] mb-2">Address type</div>
                <div className="flex gap-2">
                  {["Home", "Office", "Other"].map((t) => (
                    <button type="button" key={t} data-testid={`address-type-${t.toLowerCase()}`} onClick={() => upd("address_type", t)} className={`chip cursor-pointer ${form.address_type === t ? "!bg-[var(--yp-primary)] !text-white !border-[var(--yp-primary)]" : ""}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Delivery notes (optional)</label>
                <textarea data-testid="checkout-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows={2} className="mt-1 w-full border border-[var(--yp-border)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--yp-primary)]" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-[var(--yp-border)] rounded-2xl p-6">
            <div className="font-serif-display text-xl text-[var(--yp-primary)]">Payment</div>
            <div className="mt-4 space-y-3">
              <button
                type="button"
                data-testid="payment-online"
                onClick={() => setPaymentMethod("Online")}
                className={`w-full flex items-center gap-3 text-left border rounded-2xl p-4 ${paymentMethod === "Online" ? "border-[var(--yp-primary)] bg-[var(--yp-surface-alt)]" : "border-[var(--yp-border)]"}`}
              >
                <input type="radio" checked={paymentMethod === "Online"} readOnly className="accent-[var(--yp-primary)]" />
                <div className="flex-1">
                  <div className="font-medium">UPI / Card / Net Banking</div>
                  <div className="text-xs text-[var(--yp-muted)]">Pay securely online via Razorpay.</div>
                </div>
                <Smartphone size={20} className="text-[var(--yp-primary)]" />
              </button>
              <button
                type="button"
                data-testid="payment-cod"
                onClick={() => setPaymentMethod("COD")}
                className={`w-full flex items-center gap-3 text-left border rounded-2xl p-4 ${paymentMethod === "COD" ? "border-[var(--yp-primary)] bg-[var(--yp-surface-alt)]" : "border-[var(--yp-border)]"}`}
              >
                <input type="radio" checked={paymentMethod === "COD"} readOnly className="accent-[var(--yp-primary)]" />
                <div className="flex-1">
                  <div className="font-medium">Cash on Delivery</div>
                  <div className="text-xs text-[var(--yp-muted)]">Pay ₹{total} when your plants arrive at your doorstep.</div>
                </div>
                <Truck size={20} className="text-[var(--yp-primary)]" />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-[var(--yp-muted)]">
              <ShieldCheck size={14} /> Secured by Razorpay.
            </div>
          </section>
        </div>

        <aside className="h-fit bg-white border border-[var(--yp-border)] rounded-2xl p-6 lg:sticky lg:top-28">
          <div className="font-serif-display text-xl text-[var(--yp-primary)]">Order summary</div>
          <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
            {items.map((it) => (
              <div key={`${it.product_id}::${it.size}`} className="flex gap-3 text-sm">
                <img src={it.image} alt={it.name} className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-[var(--yp-muted)]">{it.size ? `${it.size} · ` : ""}Qty {it.quantity}</div>
                </div>
                <div className="font-medium">{inr(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 leaf-divider" />
          <div className="mt-3 text-sm space-y-2">
            <Row label="Subtotal" value={inr(subtotal)} />
            <Row label={`Shipping${shipping === 0 ? " (Free)" : ""}`} value={inr(shipping)} />
            <div className="leaf-divider my-2" />
            <Row label={<span className="font-semibold text-[var(--yp-primary)]">Total</span>} value={<span className="font-semibold text-[var(--yp-primary)] text-lg">{inr(total)}</span>} />
          </div>
          <button data-testid="btn-place-order" disabled={submitting} className="mt-5 block w-full text-center btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-70">
            {submitting ? "Placing order…" : `Place Order · ${inr(total)}`}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Field({ id, label, value, onChange, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">{label}{required && " *"}</label>
      <input data-testid={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1 w-full border border-[var(--yp-border)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--yp-primary)]" />
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between"><div>{label}</div><div>{value}</div></div>;
}
